/**
 * Personal Follow Up menu options.
 *
 * Each user keeps their own list per office via `GET|PUT /follow-up-options` (see
 * `docs/backend/leads/follow-up-options.md`). A null list means "standard Remmi options", so a user who
 * never opens the settings page still gets the defaults below — and picks up any future change
 * to them.
 */

/** One preset in the Follow Up menu on a lead. */
export interface FollowUpOption {
  /** Stable id — defaults use a fixed key, custom options a generated one. */
  id: string;
  label: string;
  /** Hidden options stay in the list (and keep their settings) but don't show in the menu. */
  isEnabled: boolean;
  /** Key into FOLLOW_UP_WHEN_OPTIONS. */
  when: string;
  /** Default time, 24h `HH:mm`. */
  time: string;
}

/** Either a fixed day count from today, or the next occurrence of a weekday. */
export interface FollowUpWhenOption {
  value: string;
  label: string;
  days?: number;
  /** 0 = Sunday … 6 = Saturday, matching `Date.getDay()`. */
  weekday?: number;
}

/** A menu row ready to render — label plus the human date/time it will set. */
export interface FollowUpMenuItem {
  id: string;
  label: string;
  display: string;
}

/** What the Follow Up menu hands back when a preset is clicked. */
export interface FollowUpSelection {
  optionId: string;
  /** Same `YYYY-MM-DDT08:00:00.000Z` date encoding the follow-up task API has always received. */
  dueDate: string;
  /** Task time in the `8:00am` format the task API already takes. */
  taskTime: string;
}

export const FOLLOW_UP_WHEN_OPTIONS: FollowUpWhenOption[] = [
  { value: 'same_day', label: 'Same day', days: 0 },
  { value: 'next_day', label: 'Next day', days: 1 },
  { value: 'days_2', label: '2 days from now', days: 2 },
  { value: 'days_3', label: '3 days from now', days: 3 },
  { value: 'days_5', label: '5 days from now', days: 5 },
  { value: 'days_7', label: '7 days from now', days: 7 },
  { value: 'days_14', label: '14 days from now', days: 14 },
  { value: 'days_21', label: '21 days from now', days: 21 },
  { value: 'days_30', label: '30 days from now', days: 30 },
  { value: 'next_monday', label: 'Next Monday', weekday: 1 },
  { value: 'next_tuesday', label: 'Next Tuesday', weekday: 2 },
  { value: 'next_wednesday', label: 'Next Wednesday', weekday: 3 },
  { value: 'next_thursday', label: 'Next Thursday', weekday: 4 },
  { value: 'next_friday', label: 'Next Friday', weekday: 5 },
  { value: 'next_saturday', label: 'Next Saturday', weekday: 6 },
  { value: 'next_sunday', label: 'Next Sunday', weekday: 0 },
];

/** 30-minute steps across the working day, 6:00 AM – 10:00 PM. */
export const FOLLOW_UP_TIME_OPTIONS: { label: string; value: string }[] = Array.from({ length: 33 }, (_, i) => {
  const minutes = 6 * 60 + i * 30;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const value = `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  const label = `${hours % 12 === 0 ? 12 : hours % 12}:${String(mins).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
  return { label, value };
});

/** The standard Remmi options — what "Reset to default" restores. */
export const DEFAULT_FOLLOW_UP_OPTIONS: FollowUpOption[] = [
  { id: 'later_today', label: 'Later Today', isEnabled: true, when: 'same_day', time: '15:00' },
  { id: 'tomorrow', label: 'Tomorrow', isEnabled: true, when: 'next_day', time: '08:00' },
  { id: 'two_days_later', label: '2 Days Later', isEnabled: true, when: 'days_2', time: '08:00' },
  { id: 'next_monday', label: 'Next Monday', isEnabled: true, when: 'next_monday', time: '08:00' },
  { id: 'next_week', label: 'Next Week', isEnabled: true, when: 'days_7', time: '08:00' },
  { id: 'two_weeks', label: '2 Weeks', isEnabled: true, when: 'days_14', time: '08:00' },
];

export function defaultFollowUpOptions(): FollowUpOption[] {
  return DEFAULT_FOLLOW_UP_OPTIONS.map(option => ({ ...option }));
}
